import { Link } from "react-router-dom";
import {
  itemPath,
  plainDescription,
  readingTime,
  tileVariantOf,
} from "../../../utilities/item-kinds";
import FramedImage from "../../../components/FramedImage/FramedImage";
import { thumbUrl } from "../../../utilities/image-urls";

export default function ArticleTile({ item }) {
  const variant = tileVariantOf(item);
  // Articles may legitimately have no image, so image variants fall
  // back to the text card until a scan is uploaded
  const isImageVariant =
    (variant === "image" || variant === "image-card") && item.image;

  if (isImageVariant) {
    return (
      <Link
        to={itemPath(item)}
        state={{ fromFeed: true }}
        className="group relative block h-full rounded-md overflow-hidden bg-cream border border-ink/5"
      >
        <FramedImage
          src={item.image}
          thumbSrc={thumbUrl(item.image)}
          alt={item.subject || "Article"}
          framing={item.framing?.tile}
          hoverZoom
          loading="lazy"
        />
        {variant === "image-card" && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent px-4 pt-8 pb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">
              Article
            </p>
            <p className="font-serif text-sm text-white leading-snug line-clamp-2">
              {item.subject || item.catalogue_id}
            </p>
          </div>
        )}
      </Link>
    );
  }

  const summary = plainDescription(item.description);

  // Text card. Every font size and spacing scales with the mosaic unit
  // --u (1px on the capped 5-column homepage, see MosaicGrid), so the
  // tile renders as the same composition at every viewport width instead
  // of fixed-size text overflowing the shrinking tile on mobile. The
  // fallback of 1px keeps the admin TilePreview (no --u) at actual size.
  // The summary grows to fill whatever height the title leaves and fades
  // out at the bottom edge rather than clamping at a fixed line count.
  return (
    <Link
      to={itemPath(item)}
      state={{ fromFeed: true }}
      className="group flex flex-col h-full rounded-md overflow-hidden bg-seal-deep text-white p-[calc(var(--u,1px)*20)]"
      style={item.tileColor ? { backgroundColor: item.tileColor } : undefined}
    >
      <p className="shrink-0 font-mono text-[length:calc(var(--u,1px)*10)] uppercase tracking-[0.2em] text-white/70 mb-[calc(var(--u,1px)*12)]">
        Article
      </p>
      <h3 className="shrink-0 font-display text-[length:calc(var(--u,1px)*20)] leading-snug line-clamp-4 group-hover:underline decoration-white/40 underline-offset-4">
        {item.subject || item.catalogue_id}
      </h3>
      <p className="flex-1 min-h-0 overflow-hidden font-serif text-[length:calc(var(--u,1px)*14)] text-white/70 leading-relaxed mt-[calc(var(--u,1px)*8)] [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5em),transparent)]">
        {summary}
      </p>
      <p className="shrink-0 font-mono text-[length:calc(var(--u,1px)*12)] text-white/60 mt-auto pt-[calc(var(--u,1px)*16)]">
        {readingTime(item.description)}{" "}
        <span
          aria-hidden="true"
          className="inline-block transition-transform group-hover:translate-x-1"
        >
          &#8594;
        </span>
      </p>
    </Link>
  );
}
